/**
 * Command Syntax Drill — Answer Matching Engine
 *
 * Pipeline: normalize → expand abbreviations → split lines →
 * compare per-line (exact then Levenshtein) → aggregate score →
 * check common mistake patterns.
 *
 * This is the most complex engine in the suite.
 */

import { expandAbbreviations } from './abbreviations';
import { levenshtein, similarity } from '../../../shared/utils/levenshtein';

/**
 * Match user input against accepted answers.
 *
 * @param {string} userInput - Raw user input (may be multi-line)
 * @param {Array} acceptedAnswers - Array of { commands: string[], explanation, is_primary }
 * @param {Array} commonMistakes - Array of { pattern, feedback }
 * @returns {Object} { score, maxScore, percentage, feedback, matchedMistakes, bestMatch }
 */
export function matchAnswer(userInput, acceptedAnswers, commonMistakes = []) {
  const normalizedInput = normalizeInput(userInput);
  const userLines = splitLines(normalizedInput);

  if (userLines.length === 0) {
    return {
      score: 0,
      maxScore: 1,
      percentage: 0,
      feedback: 'No answer provided.',
      matchedMistakes: [],
      bestMatch: null,
    };
  }

  // Try each accepted answer and pick the best match
  let bestResult = null;

  for (const answer of acceptedAnswers) {
    const normalizedAnswer = answer.commands.map(cmd => normalizeCommand(cmd));
    const result = compareLinesSet(userLines, normalizedAnswer);
    result.explanation = answer.explanation;
    result.is_primary = answer.is_primary;

    if (!bestResult || result.percentage > bestResult.percentage) {
      bestResult = result;
    }
  }

  // Check for common mistakes
  const matchedMistakes = [];
  for (const mistake of commonMistakes) {
    try {
      const regex = new RegExp(mistake.pattern, 'i');
      if (regex.test(userInput)) {
        matchedMistakes.push(mistake);
      }
    } catch {
      // If pattern isn't valid regex, try substring match
      if (userInput.toLowerCase().includes(mistake.pattern.toLowerCase())) {
        matchedMistakes.push(mistake);
      }
    }
  }

  return {
    ...bestResult,
    matchedMistakes,
  };
}

/**
 * Normalize raw user input.
 */
function normalizeInput(input) {
  return input
    .trim()
    .replace(/\r\n/g, '\n')
    .replace(/\t/g, ' ');
}

/**
 * Normalize a single command line.
 * Lowercase, collapse whitespace, expand abbreviations.
 */
function normalizeCommand(cmd) {
  let result = cmd.toLowerCase().trim();
  result = result.replace(/\s+/g, ' ');
  result = expandAbbreviations(result);
  return result;
}

/**
 * Split input into command lines, filtering empties.
 */
function splitLines(input) {
  return input
    .split('\n')
    .map(line => normalizeCommand(line))
    .filter(line => line.length > 0);
}

/**
 * Compare two sets of command lines.
 * Each expected line tries to find its best match in user lines.
 */
function compareLinesSet(userLines, expectedLines) {
  const maxScore = expectedLines.length;
  let totalScore = 0;
  const lineResults = [];
  const usedUserLines = new Set();

  for (const expected of expectedLines) {
    let bestScore = 0;
    let bestLine = null;
    let bestIdx = -1;

    for (let i = 0; i < userLines.length; i++) {
      if (usedUserLines.has(i)) continue;

      const sim = similarity(userLines[i], expected);
      if (sim > bestScore) {
        bestScore = sim;
        bestLine = userLines[i];
        bestIdx = i;
      }
    }

    // Scoring thresholds
    let lineScore;
    if (bestScore >= 0.95) {
      lineScore = 1; // Exact or near-exact match
    } else if (bestScore >= 0.7) {
      lineScore = 0.5; // Partial match
    } else {
      lineScore = 0; // No match
    }

    totalScore += lineScore;
    if (bestIdx >= 0 && lineScore > 0) {
      usedUserLines.add(bestIdx);
    }

    lineResults.push({
      expected,
      userLine: bestLine,
      score: lineScore,
      similarity: bestScore,
    });
  }

  // Generate feedback
  const feedback = generateFeedback(lineResults);

  return {
    score: totalScore,
    maxScore,
    percentage: maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0,
    feedback,
    lineResults,
  };
}

/**
 * Generate human-readable feedback from line comparison results.
 */
function generateFeedback(lineResults) {
  if (lineResults.every(r => r.score === 1)) {
    return 'Correct! All commands match.';
  }

  const issues = [];
  for (const result of lineResults) {
    if (result.score === 0) {
      if (result.userLine) {
        issues.push(`Missing: \`${result.expected}\` (you wrote: \`${result.userLine}\`)`);
      } else {
        issues.push(`Missing: \`${result.expected}\``);
      }
    } else if (result.score === 0.5) {
      issues.push(`Close: \`${result.expected}\` (you wrote: \`${result.userLine}\`)`);
    }
  }

  return issues.join('\n');
}

/**
 * Quick check if an answer is fully correct (100%).
 */
export function isFullyCorrect(result) {
  return result.percentage === 100;
}
